import { Box, Grid, GridItem, Text, Tooltip } from '@chakra-ui/react'
import moment from 'moment'
import numeral from 'numeral'
import React, { useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { getExtensionFromFileTypeAndMimeType, formatDuration } from '../../../../../utils/mediaLibrary'
import type { MediaLibrarySearchFileResponse } from '../../../../interfaces'
import type { FileDetailsResponse } from '../../../../interfaces/FileDetailsResponse'
import { PublicLinksInfoContext } from '../../../../providers/PublicLinksInfoProvider'
import FileDescription from '../FileDescription'

interface FileInfoProps {
  fileDetails: FileDetailsResponse | MediaLibrarySearchFileResponse
}

const FileInfo: React.FC<FileInfoProps> = ({ fileDetails }) => {
  const { t } = useTranslation()
  const { isPublicLink } = useContext(PublicLinksInfoContext)

  const createdBy = (fileDetails as FileDetailsResponse).createdBy?.name
  const userEmail = (fileDetails as FileDetailsResponse).createdBy?.email
  const fileExtension = getExtensionFromFileTypeAndMimeType(
    fileDetails as FileDetailsResponse,
  )

  const yesNo = useMemo(
    () => (v?: boolean) => v ? t('yes', 'Yes') : t('no', 'No'),
    [t],
  )

  return (
    <Box>
      <Text fontWeight="600" mb="12px">
        {t('file_info', 'File info')}
      </Text>

      <Grid
        templateColumns="160px 1fr"
        rowGap="12px"
        columnGap="16px"
        alignItems="center"
      >
        {(fileDetails as FileDetailsResponse).width &&
        (fileDetails as FileDetailsResponse).height ? (
          <>
            <GridItem>
              <Text color="#4b5a74">{t('Dimensions', 'Dimensions')}</Text>
            </GridItem>
            <GridItem>
              <Tooltip
                label={`${(fileDetails as FileDetailsResponse).width} x ${
                  (fileDetails as FileDetailsResponse).height
                }`}
              >
                <Text fontWeight="500" className="width">
                  {(fileDetails as FileDetailsResponse).width} x{' '}
                  {(fileDetails as FileDetailsResponse).height}
                </Text>
              </Tooltip>
            </GridItem>
          </>
        ) : null}

        <GridItem>
          <Text color="#4b5a74">{t('Size', 'Size')}</Text>
        </GridItem>
        <GridItem>
          <Text fontWeight="500" className="file-size">
            {numeral((fileDetails as FileDetailsResponse).size)
              .format('0.00 ib')
              .replace('i', '')}
          </Text>
        </GridItem>

        {fileExtension ? (
          <>
            <GridItem>
              <Text color="#4b5a74">{t('Format', 'Format')}</Text>
            </GridItem>
            <GridItem>
              <Text fontWeight="500" className="format">
                {fileExtension.toUpperCase()}
              </Text>
            </GridItem>
          </>
        ) : null}

        <GridItem>
          <Text color="#4b5a74">{t('Created', 'Created')}</Text>
        </GridItem>
        <GridItem>
          <Text fontWeight="500" className="created-at">
            {(fileDetails as FileDetailsResponse).createdAt ? moment((fileDetails as FileDetailsResponse).createdAt).format('MMMM Do YYYY') : "-"}
          </Text>
        </GridItem>

        {!isPublicLink && (createdBy || userEmail) ? (
          <>
            <GridItem>
              <Text color="#4b5a74">{t('creator', 'Creator')}</Text>
            </GridItem>
            <GridItem>
              <Tooltip label={userEmail}>
                <Text fontWeight="500" className="created-by">
                  {createdBy || userEmail}
                </Text>
              </Tooltip>
            </GridItem>
          </>
        ) : null}

        {!isPublicLink ? (
          <>
            <GridItem>
              <Text color="#4b5a74">{t('private_file', 'Private file')}</Text>
            </GridItem>
            <GridItem>
              <Text fontWeight="500">
                {yesNo((fileDetails as FileDetailsResponse).isPrivateFile)}
              </Text>
            </GridItem>
          </>
        ) : null}

        {!isPublicLink ? (
          <>
            <GridItem>
              <Text color="#4b5a74">{t('published', 'Published')}</Text>
            </GridItem>
            <GridItem>
              <Text fontWeight="500">
                {yesNo((fileDetails as FileDetailsResponse).isPublished)}
              </Text>
            </GridItem>
          </>
        ) : null}

        {fileDetails.mime?.startsWith('video/') && typeof fileDetails.duration === 'number' && fileDetails.duration > 0 ? (
          <>
            <GridItem>
              <Text color="#4b5a74">{t('Duration', 'Duration')}</Text>
            </GridItem>
            <GridItem>
              <Text fontWeight="500">
                {formatDuration(fileDetails.duration)}
              </Text>
            </GridItem>
          </>
        ) : null}

        <GridItem alignSelf="flex-start">
          <Text color="#4b5a74">{t('Description', 'Description')}</Text>
        </GridItem>
        <GridItem>
          <FileDescription fileDetails={fileDetails as FileDetailsResponse} />
        </GridItem>
      </Grid>
    </Box>
  )
}

export default FileInfo
